// The practice controls the band's Tempo panel and the standalone metronome
// share: the tempo stepper (−, the typed field, TAP, +, the slider), the drum
// loop picker with its rate and time-stretch switch, and the labelled field the
// speed trainers sit in. One set of controls, so the two places look and behave
// as one feature. Everything here is drawn in the transport's control language
// (`.transport-bar` or `.transport-surface` supplies the tokens).

import { useMemo, useRef, useState, type ReactNode } from 'react';
import type { DrumLoop } from '@/audio/drumLoops';
import { TEMPO_MAX, TEMPO_MIN, useStore } from '@/state/store';
import { NumberField } from './NumberField';
import { MixerSoundControl } from './MixerControls';
import { DRUM_LOOP_SEARCH, drumLoopOptions } from './drumLoopOptions';
import { TransportControlButton } from './TransportBar';
import './PracticeControls.css';

const clampTempo = (n: number) => Math.max(TEMPO_MIN, Math.min(TEMPO_MAX, Math.round(n)));

/** Tap-tempo: tap in time and set the BPM from the average tap interval. Resets
 * the window after a >2s gap (a fresh count-off) and keeps the last few taps so
 * the reading settles as you go. */
export function TapTempo({
  setTempo,
  disabled = false,
}: {
  setTempo: (n: number) => void;
  disabled?: boolean;
}) {
  const taps = useRef<number[]>([]);
  const [count, setCount] = useState(0);
  const onTap = () => {
    const now = performance.now();
    const t = taps.current;
    if (t.length && now - t[t.length - 1] > 2000) t.length = 0; // new count-off
    t.push(now);
    if (t.length > 6) t.shift();
    if (t.length >= 2) {
      const avg = (t[t.length - 1] - t[0]) / (t.length - 1);
      setTempo(clampTempo(60000 / avg));
    }
    setCount(t.length);
  };
  return (
    <TransportControlButton
      className="transport-tap-control"
      onClick={onTap}
      disabled={disabled}
      data-testid="tap-tempo"
      aria-label="Tap tempo"
      title="Tap in time to set the tempo"
    >
      {count >= 2 ? '♩' : 'TAP'}
    </TransportControlButton>
  );
}

/** The tempo field: its label, − / typed / TAP / + in a row, and the slider. */
export function TempoStepper({
  id,
  label,
  tempo,
  setTempo,
  disabled = false,
}: {
  id: string;
  label: string;
  tempo: number;
  setTempo: (n: number) => void;
  disabled?: boolean;
}) {
  return (
    <>
      <label className="tempo-field-label" htmlFor={id}>
        {label}
      </label>
      <div className="panel-stepper">
        <TransportControlButton
          aria-label="Slower"
          disabled={disabled}
          onClick={() => setTempo(clampTempo(tempo - 4))}
        >
          −
        </TransportControlButton>
        <NumberField
          id={id}
          className="tempo-number-field"
          value={tempo}
          min={TEMPO_MIN}
          max={TEMPO_MAX}
          step={1}
          disabled={disabled}
          onCommit={setTempo}
          aria-label="Tempo (BPM)"
        />
        <TapTempo setTempo={setTempo} disabled={disabled} />
        <TransportControlButton
          aria-label="Faster"
          disabled={disabled}
          onClick={() => setTempo(clampTempo(tempo + 4))}
        >
          +
        </TransportControlButton>
      </div>
      <input
        type="range"
        className="tempo-slider panel-slider"
        min={TEMPO_MIN}
        max={TEMPO_MAX}
        step={1}
        value={tempo}
        disabled={disabled}
        onChange={(e) => setTempo(Number(e.target.value))}
        aria-label="Tempo slider"
      />
    </>
  );
}

/**
 * The drum loop picker: the loops under their sections, a search, and a list
 * that stays open after a pick, so the next loop is a press away (user, Oct
 * 2026: "keep the drop down open so it's easy to change loops again").
 * `noneLabel` offers no loop; `placeholder` names a value the bands disagree on.
 */
export function DrumLoopMenu({
  loops,
  value,
  onChange,
  noneLabel,
  placeholder,
  testid = 'drum-loop',
  className,
  disabled,
}: {
  loops: readonly DrumLoop[];
  value: string | null;
  onChange: (key: string | null) => void;
  noneLabel?: string;
  placeholder?: string;
  testid?: string;
  /** The trigger's look where no panel styles it ('band-mixer-sound-select'). */
  className?: string;
  disabled?: boolean;
}) {
  const options = useMemo(
    () => [
      ...(noneLabel === undefined ? [] : [{ id: '', label: noneLabel }]),
      ...drumLoopOptions(loops),
    ],
    [loops, noneLabel],
  );
  const current = value === null ? undefined : loops.find((loop) => loop.key === value);
  return (
    <MixerSoundControl
      sound={current?.label ?? (value === null ? (placeholder ?? noneLabel) : null) ?? 'No loop'}
      label="Drum loop"
      value={value ?? ''}
      options={options}
      onChange={(key) => onChange(key === '' ? null : key)}
      search={DRUM_LOOP_SEARCH}
      keepOpen
      testid={testid}
      className={className}
      disabled={disabled}
    />
  );
}

/** Time-stretch for every drum loop: keep the recording's pitch at any tempo. */
export function DrumTimeStretch() {
  const drumTimeStretch = useStore((s) => s.drumTimeStretch);
  const setDrumTimeStretch = useStore((s) => s.setDrumTimeStretch);
  return (
    <>
      <label className="switch">
        <input
          type="checkbox"
          checked={drumTimeStretch}
          onChange={(e) => setDrumTimeStretch(e.target.checked)}
          data-testid="drum-time-stretch"
        />
        Time stretch
      </label>
    </>
  );
}

/**
 * Match loop: set the tempo that plays the drum loop at its recorded speed,
 * through the practice speed. Idle once it already does.
 */
export function MatchLoopButton({
  loopBpm,
  effectiveTempo,
  activePct,
  disabled,
  setTempo,
  testid = 'tempo-match-loop',
}: {
  loopBpm: number;
  effectiveTempo: number;
  activePct: number;
  disabled: boolean;
  setTempo: (n: number) => void;
  testid?: string;
}) {
  const matched = Math.round((loopBpm * 100) / Math.max(1, activePct));
  return (
    <button
      type="button"
      className="btn btn-sm"
      data-testid={testid}
      disabled={disabled || loopRate(effectiveTempo, loopBpm) === 100}
      onClick={() => setTempo(clampTempo(matched))}
    >
      Match loop
    </button>
  );
}

const loopRate = (effectiveTempo: number, loopBpm: number) =>
  Math.round((effectiveTempo / loopBpm) * 100);

/**
 * How the tempo sits against the drum loop's own (#421): a loop far from 100%
 * sounds stretched or sped up, so the reading says where it is beside Match loop.
 */
export function DrumLoopRate(props: {
  loopBpm: number;
  effectiveTempo: number;
  activePct: number;
  disabled: boolean;
  setTempo: (n: number) => void;
}) {
  return (
    <div className="tempo-loop-rate" data-testid="tempo-loop-rate">
      <span>
        Loop ♩={props.loopBpm} · <strong>{loopRate(props.effectiveTempo, props.loopBpm)}%</strong>
      </span>
      <MatchLoopButton {...props} />
    </div>
  );
}

/** A small uppercase label over its field, as the trainer grids use. */
export function FormField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="control-group">
      <span className="label">{label}</span>
      {children}
    </div>
  );
}
